import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listWebhookEvents, replayWebhookEvent } from '../../services/liveStamping';
import { toast } from 'react-toastify';
import { useClientUsers } from '../../hooks/useClientUsers';
import { formatClientOptionLabel } from '../../utils/clientTenantLabel';
import { formatDateTimeIST } from '../../utils/dateUtils';

const STATUS_COLORS = {
  SUCCESS: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  PENDING: 'bg-yellow-100 text-yellow-700',
  PROCESSING: 'bg-blue-100 text-blue-700',
};

const EVENT_TYPES = [
  'order.placed',
  'order.executing',
  'order.metadata_ready',
  'order.completed',
  'order.rejected',
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];

/** Filters applied after Apply and on Clear reset. Default: Failed only (no “all statuses”). */
export const WEBHOOK_LOG_DEFAULT_FILTERS = {
  tenantId: '',
  eventType: '',
  entityId: '',
  status: 'FAILED',
};

/**
 * Admin delivery log — GET /api/admin/webhooks/events.
 * @param {object} props
 * @param {object[]} [props.clients] Client users for tenant dropdown; omit to load GET /api/users internally.
 * @param {boolean} [props.clientsLoading]
 */
const AdminWebhookDeliveryLog = ({ clients: externalClients, clientsLoading: externalClientsLoading }) => {
  const selfFetch = useClientUsers({ enabled: externalClients === undefined });
  const tenantClientsList = externalClients !== undefined ? externalClients : selfFetch.clients;
  const tenantClientsBusy =
    externalClients !== undefined ? Boolean(externalClientsLoading) : selfFetch.loading;

  const sortedTenantOptions = useMemo(
    () =>
      [...tenantClientsList]
        .filter((c) => c.id || c._id)
        .sort((a, b) =>
          formatClientOptionLabel(a).localeCompare(formatClientOptionLabel(b), undefined, {
            sensitivity: 'base',
          })
        ),
    [tenantClientsList]
  );

  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState(WEBHOOK_LOG_DEFAULT_FILTERS);
  const [pendingFilters, setPendingFilters] = useState(WEBHOOK_LOG_DEFAULT_FILTERS);
  const [inspectEvent, setInspectEvent] = useState(null);
  const [replayingId, setReplayingId] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: pageSize, offset: page * pageSize };
      if (filters.tenantId) params.tenantId = filters.tenantId;
      if (filters.eventType) params.eventType = filters.eventType;
      if (filters.entityId) params.entityId = filters.entityId;
      if (filters.status) params.status = filters.status;
      const res = await listWebhookEvents(params);
      setEvents(res.events || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load events.');
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize) || 1),
    [total, pageSize]
  );

  useEffect(() => {
    const maxIdx = totalPages - 1;
    if (page > maxIdx) setPage(maxIdx);
  }, [totalPages, page]);

  const applyFilters = () => {
    setFilters(pendingFilters);
    setPage(0);
  };

  const clearFilters = () => {
    const reset = { ...WEBHOOK_LOG_DEFAULT_FILTERS };
    setPendingFilters(reset);
    setFilters(reset);
    setPage(0);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(0);
  };

  const handleReplay = async (event) => {
    setReplayingId(event.id);
    try {
      const res = await replayWebhookEvent(event.id);
      toast.success(`Re-queued as new event ${res.newEventId}`);
      fetchEvents();
    } catch (err) {
      toast.error(err.message || 'Replay failed.');
    } finally {
      setReplayingId(null);
    }
  };

  const startRow = total === 0 ? 0 : page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, total);

  const pageNumbersToShow = useMemo(() => {
    const maxButtons = 7;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(0, page - half);
    let end = Math.min(totalPages - 1, start + maxButtons - 1);
    if (end - start < maxButtons - 1) {
      start = Math.max(0, end - maxButtons + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [totalPages, page]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Filter bar */}
      <div className="border-b">
      <div className="px-5 pt-3 pb-2 flex flex-wrap items-end gap-3">
        <div className="min-w-[280px] max-w-[min(420px,100vw-2rem)]">
          <label className="block text-xs text-gray-500 mb-1">Tenant (client)</label>
          <select
            value={pendingFilters.tenantId}
            onChange={(e) => setPendingFilters((f) => ({ ...f, tenantId: e.target.value }))}
            disabled={tenantClientsBusy}
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-50"
          >
            <option value="">
              {tenantClientsBusy ? 'Loading clients…' : 'All clients'}
            </option>
            {sortedTenantOptions.map((c) => {
              const tid = String(c.id ?? c._id);
              return (
                <option key={tid} value={tid}>
                  {formatClientOptionLabel({ ...c, id: tid })}
                </option>
              );
            })}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Event Type</label>
          <select
            value={pendingFilters.eventType}
            onChange={(e) => setPendingFilters((f) => ({ ...f, eventType: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 min-w-[9rem]"
          >
            <option value="">All events</option>
            {EVENT_TYPES.map((et) => (
              <option key={et} value={et}>
                {et}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Entity ID</label>
          <input
            type="text"
            value={pendingFilters.entityId}
            onChange={(e) => setPendingFilters((f) => ({ ...f, entityId: e.target.value }))}
            placeholder="Filter by entity…"
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 w-44"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={pendingFilters.status}
            onChange={(e) => setPendingFilters((f) => ({ ...f, status: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 min-w-[9rem]"
          >
            <option value="">All statuses</option>
            <option value="PENDING">Pending</option>
            <option value="PROCESSING">Processing</option>
            <option value="SUCCESS">Success</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>
        <div className="flex gap-2 pb-0.5">
          <button
            type="button"
            onClick={applyFilters}
            className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={fetchEvents}
            disabled={loading}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>
      <p className="px-5 pb-3 text-xs text-gray-400">
        Default filters show <span className="font-medium text-gray-600">FAILED</span> deliveries until you change them —
        {' '}<strong>Reset</strong> restores that default.
      </p>
      </div>

      {error && (
        <div className="mx-5 mt-3 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Created At</th>
              <th className="px-4 py-3 text-left">Tenant ID</th>
              <th className="px-4 py-3 text-left">Event Type</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Retries</th>
              <th className="px-4 py-3 text-left">Reason</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && events.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : events.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  No events found.
                </td>
              </tr>
            ) : (
              events.map((ev) => (
                <tr
                  key={ev.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setInspectEvent(ev)}
                >
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                    {ev.createdAt ? formatDateTimeIST(ev.createdAt) : '—'}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700 max-w-[120px] truncate">
                    {ev.tenantId || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-700">{ev.eventType || '—'}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        STATUS_COLORS[ev.status] || 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {ev.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {ev.retryCount ?? 0} / {ev.maxRetries ?? 0}
                  </td>
                  <td
                    className="px-4 py-3 text-xs text-gray-500 max-w-[180px] truncate"
                    title={ev.reason || ''}
                  >
                    {ev.reason ? (ev.reason.length > 40 ? ev.reason.slice(0, 40) + '…' : ev.reason) : '—'}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => handleReplay(ev)}
                      disabled={replayingId === ev.id}
                      className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                    >
                      {replayingId === ev.id ? 'Queuing…' : 'Replay'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-5 py-3 border-t flex flex-wrap items-center justify-between gap-4 text-sm text-gray-600 bg-gray-50/40">
        <div className="flex flex-wrap items-center gap-3">
          <span>
            Showing <strong>{startRow}</strong>–<strong>{endRow}</strong> of <strong>{total}</strong>
          </span>
          <label className="inline-flex items-center gap-2 text-xs">
            <span className="text-gray-500">Per page</span>
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={() => setPage(0)}
            disabled={page === 0 || loading}
            className="px-2 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-40 text-xs"
            title="First page"
          >
            «
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
            className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-40"
          >
            ← Prev
          </button>

          {totalPages > 1 && (
            <>
              <div className="hidden sm:flex flex-wrap items-center justify-center gap-0.5 px-2">
                {pageNumbersToShow.map((pNum) => (
                  <button
                    key={pNum}
                    type="button"
                    onClick={() => setPage(pNum)}
                    disabled={loading}
                    className={`min-w-[2rem] px-2 py-1 rounded text-xs border ${
                      pNum === page
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {pNum + 1}
                  </button>
                ))}
              </div>
              <span className="sm:hidden text-xs text-gray-500 px-2">
                Page {page + 1} / {totalPages}
              </span>
            </>
          )}

          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1 || loading || total === 0}
            className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-40"
          >
            Next →
          </button>
          <button
            type="button"
            onClick={() => setPage(Math.max(0, totalPages - 1))}
            disabled={page >= totalPages - 1 || loading || total === 0}
            className="px-2 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-40 text-xs"
            title="Last page"
          >
            »
          </button>
        </div>
      </div>

      {/* Inspector modal */}
      {inspectEvent && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setInspectEvent(null)}
          role="presentation"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div className="px-6 py-4 border-b flex items-center justify-between shrink-0">
              <h3 className="font-bold text-gray-800">Event Inspector</h3>
              <button type="button" onClick={() => setInspectEvent(null)} className="text-gray-400 hover:text-gray-600 text-xl">
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <dl className="space-y-3 text-sm">
                {[
                  ['ID', inspectEvent.id],
                  ['Tenant ID', inspectEvent.tenantId],
                  ['Event Type', inspectEvent.eventType],
                  ['Event Sub-type', inspectEvent.eventSubType],
                  ['Entity Name', inspectEvent.entityName],
                  ['Entity ID', inspectEvent.entityId],
                  ['External Ref ID', inspectEvent.externalRefId],
                  ['Status', inspectEvent.status],
                  ['Retry Count', `${inspectEvent.retryCount ?? 0} / ${inspectEvent.maxRetries ?? 0}`],
                  ['Reason', inspectEvent.reason || '—'],
                  ['Created At', inspectEvent.createdAt ? formatDateTimeIST(inspectEvent.createdAt) : '—'],
                  ['Processed At', inspectEvent.processedAt ? formatDateTimeIST(inspectEvent.processedAt) : '—'],
                  ['Last Retried At', inspectEvent.lastRetriedAt ? formatDateTimeIST(inspectEvent.lastRetriedAt) : '—'],
                  ['Next Retry At', inspectEvent.nextRetryAt ? formatDateTimeIST(inspectEvent.nextRetryAt) : '—'],
                ].map(([label, value]) => (
                  <div key={label} className="flex gap-3">
                    <dt className="w-36 shrink-0 text-xs text-gray-500">{label}</dt>
                    <dd className="font-mono text-xs text-gray-800 break-all">{value ?? '—'}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button
                type="button"
                onClick={() => setInspectEvent(null)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWebhookDeliveryLog;
